/**
 * Pure edit-state evaluator for the file/folder display dialog
 * (`<FileDisplayDetailsDialog>`, workspace-file-tags). Split out so the tag
 * grammar (C3) + case-scope tag-uniqueness pre-check + title/description
 * validation + pristine/save-enabled logic is unit-testable without a DOM
 * harness. Mirrors what the server (`document.set_display`, C5) enforces, so the
 * lawyer gets inline feedback before the round-trip; the server stays
 * authoritative.
 */

import {
  validateAlias,
  validateTag,
  validateTitle,
  validateDescription,
  findDisplayNameCollision,
  normalizeTagKey,
  type DisplayValidation,
  type TagValidation,
} from "@/lib/workspace/display-name";

// ── Folder path (alias overlay — folders are NOT tagged; §12) ─────────────────
// Folders keep the shipped alias + description overlay; the dialog uses this
// evaluator on its folder branch. Files use the tag evaluator below.

export interface DisplayEditInput {
  /** Current value of the name (alias) field. */
  alias: string;
  description: string;
  currentAlias?: string;
  currentDescription?: string;
  /** Effective display names of the node's SIBLINGS (excludes this node) — D3. */
  siblingEffectiveNames: readonly string[];
}

export interface DisplayEditState {
  isClearingAlias: boolean;
  aliasError?: DisplayValidation["error"];
  descriptionError?: DisplayValidation["error"];
  conflictWith: string | null;
  dirty: boolean;
  canSave: boolean;
}

export function evaluateDisplayEdit(input: DisplayEditInput): DisplayEditState {
  const aliasTrimmed = input.alias.trim();
  const isClearingAlias = aliasTrimmed === "";
  const aliasError = isClearingAlias ? undefined : validateAlias(input.alias).error;
  const conflictWith =
    isClearingAlias || aliasError
      ? null
      : findDisplayNameCollision(aliasTrimmed, input.siblingEffectiveNames);
  const descriptionError = validateDescription(input.description).error;
  const dirty =
    aliasTrimmed !== (input.currentAlias?.trim() ?? "") ||
    input.description.trim() !== (input.currentDescription?.trim() ?? "");
  const canSave = dirty && !aliasError && !descriptionError && !conflictWith;
  return { isClearingAlias, aliasError, descriptionError, conflictWith, dirty, canSave };
}

export function toDisplayPatchFields(alias: string, description: string): {
  alias: string | null;
  description: string | null;
} {
  const a = alias.trim();
  const d = description.trim();
  return { alias: a === "" ? null : a, description: d === "" ? null : d };
}

export interface TagEditInput {
  /** Current value of the Tag field (a leading `@` is tolerated). */
  tag: string;
  /** Current value of the Título field (the citation name). */
  title: string;
  /** Current value of the Descrição field. */
  description: string;
  /** The node's stored tag body (no `@`), for pristine detection. */
  currentTag?: string;
  /** The node's stored title, for pristine detection. */
  currentTitle?: string;
  /** The node's stored description, for pristine detection. */
  currentDescription?: string;
  /**
   * Normalized keys of every OTHER file's tag in the case (EXCLUDES this node) —
   * the case-scope uniqueness pre-check (C3/§7). The server is authoritative.
   */
  otherTagKeys: ReadonlySet<string>;
}

export interface TagEditState {
  /** Grammar error on the (non-empty) tag, else undefined (C5 `invalid_tag` reason). */
  tagError?: NonNullable<TagValidation["reason"]>;
  /** True when the (valid) tag collides with another file's tag (C5 `tag_conflict`). */
  tagConflict: boolean;
  /** Validation error on the (non-empty) title. */
  titleError?: NonNullable<DisplayValidation["error"]>;
  /** Validation error on the description. */
  descriptionError?: NonNullable<DisplayValidation["error"]>;
  /** Something changed vs the stored values. */
  dirty: boolean;
  /** Valid + dirty → the Save button is enabled. */
  canSave: boolean;
}

export function evaluateTagEdit(input: TagEditInput): TagEditState {
  // An EMPTY tag field = "leave the tag unchanged" — Stage A cannot clear a tag
  // (`tag: null` is server-rejected), so a blank field is never a validation
  // error; it just omits the tag from the patch (lets a pre-reseed file edit its
  // title/description without inventing a tag).
  const tagBody = input.tag.trim().replace(/^@/, "");
  const isSettingTag = tagBody !== "";
  const tagValidation = isSettingTag ? validateTag(tagBody) : ({ ok: true } as TagValidation);
  const tagError = tagValidation.ok ? undefined : tagValidation.reason;
  const tagKey = isSettingTag && !tagError ? normalizeTagKey(tagBody) : "";
  const tagConflict = tagKey !== "" && input.otherTagKeys.has(tagKey);

  const titleTrimmed = input.title.trim();
  const titleError = titleTrimmed === "" ? undefined : validateTitle(input.title).error;
  const descriptionError = validateDescription(input.description).error;

  const currentTagBody = (input.currentTag ?? "").trim();
  const tagChanged = isSettingTag && tagBody !== currentTagBody;
  const titleChanged = titleTrimmed !== (input.currentTitle?.trim() ?? "");
  const descriptionChanged =
    input.description.trim() !== (input.currentDescription?.trim() ?? "");
  const dirty = tagChanged || titleChanged || descriptionChanged;

  const canSave = dirty && !tagError && !tagConflict && !titleError && !descriptionError;

  return { tagError, tagConflict, titleError, descriptionError, dirty, canSave };
}

/**
 * Build the `document.set_display` patch fields from the raw inputs.
 * - `tag`: a non-empty body SETS (rename-with-redirect on the server); an empty
 *   field is `undefined` = OMIT (never `null` — clearing is server-rejected in
 *   Stage A).
 * - `title` / `description`: a non-empty trimmed value SETS; an empty value
 *   CLEARS (`null`).
 */
export function toTagPatchFields(
  tag: string,
  title: string,
  description: string,
): { tag: string | undefined; title: string | null; description: string | null } {
  const t = tag.trim().replace(/^@/, "");
  const ti = title.trim();
  const d = description.trim();
  return {
    tag: t === "" ? undefined : t,
    title: ti === "" ? null : ti,
    description: d === "" ? null : d,
  };
}
